import { Button, Empty, DatePicker, Tag, FormTitle } from '@maxtropy/components';
import { Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { EnergyMediumSceneId, ImgEnumDisplay, UrlEnumDisplay } from '../ProcessingEfficiency';
import styles from './index.module.scss';
import { EnergyMediumSceneIds } from '@/api/energyMedium';
import LossEcharts from '../LossEcharts';
import { DoubleRightOutlined, SettingOutlined } from '@ant-design/icons';
import LossRateSettingModal from '../LossRateSettingModal';
import { isNil } from 'lodash-es';
import { max } from '@/pages/EnergyMediaComparison/utils';
import LossDetailModal from '../LossDetailModal';
import {
  apiV2EnergyCompareGetMediumIndicatorLossDataPost,
  apiV2MediumIndicatorDashboardGetIndicatorDisplayUnitPost,
  V2EnergyCompareGetMediumIndicatorLossDataPostResponse,
  V2MediumIndicatorDashboardGetIndicatorDisplayUnitPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import SwitchDate from './components/SwitchDate';
import { RangeValue, TimeActive } from './type';
import {
  defaultNodeRangeValue,
  formatByKeyDate,
  getDefaultActiveKey,
  getDefaultRangeDate,
  onChangePicker,
  TimePickerUtil,
} from './util';
import { useSearchParams } from 'react-router-dom';

export interface ILossDuringTransAndDisProcessProps {
  processId?: React.Key;
  selectedTopoSceneId?: number;
  selectedProcessName?: string;
  topoId?: number;
}
export type LossDataPostResponse = V2EnergyCompareGetMediumIndicatorLossDataPostResponse['viewVoTos'];
export type LossDataPostItemResponse = Exclude<
  V2EnergyCompareGetMediumIndicatorLossDataPostResponse['viewVoTos'],
  undefined
>[number];

const uetUnitType = 5; // 拓扑类型

const LossDuringTransAndDisProcess: FC<ILossDuringTransAndDisProcessProps> = props => {
  const RangePicker = DatePicker.RangePicker;
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const from = urlSearchParams.get('from');
  const to = urlSearchParams.get('to');
  const urlActiveKey = urlSearchParams.get('activeKey') as TimeActive;
  const { processId, selectedTopoSceneId, selectedProcessName, topoId } = props;
  const [rangeDate, setRangeDate] = useState<[Dayjs, Dayjs]>(getDefaultRangeDate(from ?? undefined, to ?? undefined)); // datepicker的时间范围
  const [statisticsDataLoading, setStatisticsDataLoading] = useState<boolean>(false);
  const [processStatistics, setProcessStatistics] = useState<LossDataPostResponse>();
  const [lossRateSettingVisible, setLossRateSettingVisible] = useState<boolean>(false);
  const [mediumIndicatorDisplayUnitData, setMediumIndicatorDisplayUnitData] = useState<
    V2MediumIndicatorDashboardGetIndicatorDisplayUnitPostResponse['list']
  >([]);
  const [lossDetailVisible, setLossDetailVisible] = useState<boolean>(false);
  const [currentSelectedLossBasicDetail, SetCurrentSelectedLossBasicDetail] = useState<LossDataPostItemResponse>();
  const [activeKey, setActiveKey] = useState<TimeActive>(getDefaultActiveKey(urlActiveKey)); // 选择的switch时间范围
  const picker = useMemo(() => TimePickerUtil(activeKey), [activeKey]);

  useEffect(() => {
    setUrlSearchParams(
      current => {
        const next = new URLSearchParams(current);
        next.set('from', String(rangeDate[0].valueOf()));
        next.set('to', String(rangeDate[1].valueOf()));
        next.set('activeKey', activeKey);
        return next;
      },
      { replace: true }
    );
  }, [activeKey, rangeDate, setUrlSearchParams]);

  // 单位
  useEffect(() => {
    if (!topoId) return;
    apiV2MediumIndicatorDashboardGetIndicatorDisplayUnitPost({
      uetUnitType: uetUnitType,
      uetUnitId: topoId,
    }).then(res => {
      setMediumIndicatorDisplayUnitData(res.list ?? []);
    });
  }, [topoId]);

  useEffect(() => {
    if (processId) {
      setStatisticsDataLoading(true);
      let timeResolution = 'D';
      if (activeKey === TimeActive.HOUR) {
        timeResolution = 'H';
      } else if (activeKey === TimeActive.THIS_WEEK) {
        timeResolution = 'W';
      } else if (activeKey === TimeActive.THIS_MONTH) {
        timeResolution = 'M';
      }
      apiV2EnergyCompareGetMediumIndicatorLossDataPost({
        processId: Number(processId),
        from: rangeDate[0].startOf('day').valueOf(),
        to: rangeDate[1].endOf('day').valueOf(),
        timeResolution,
      }).then(res => {
        setProcessStatistics(res.viewVoTos ?? []);
        setStatisticsDataLoading(false);
      });
    }
  }, [processId, rangeDate, activeKey]);

  const navigateToEnergyMediumDashboard = () => {
    const jumpUrl = UrlEnumDisplay[selectedTopoSceneId as EnergyMediumSceneId];
    window.open(
      `${jumpUrl}?processId=${processId}&timeResolution=D&from=${rangeDate[0]
        .startOf('day')
        .valueOf()}&to=${rangeDate[1].endOf('day').valueOf()}`
    );
  };

  const onShowLossDetail = (item: LossDataPostItemResponse) => {
    SetCurrentSelectedLossBasicDetail(item);
    setLossDetailVisible(true);
  };

  const unitDisplay = useCallback(
    (indicatorId?: number) => {
      return (mediumIndicatorDisplayUnitData ?? []).find(i => i.energyMediumIndicatorId === indicatorId)
        ?.displayPhysicalUnitGeneralName;
    },
    [mediumIndicatorDisplayUnitData]
  );

  const dateShow = (filterLossDataList: LossDataPostItemResponse['filterLossDataList']) => {
    console.log('filterLossDataList :>> ', filterLossDataList);
    const formattedDates = (filterLossDataList ?? [])
      .sort((a, b) => (a.lossRate ?? 0) - (b.lossRate ?? 0))
      .map(i => formatByKeyDate(i.ts, activeKey));
    if (formattedDates.length > 3) {
      return formattedDates.slice(0, 3).join(' 、 ') + '等';
    } else {
      return formattedDates.join(' 、 ');
    }
  };

  return (
    <>
      <FormTitle
        title={'输配节点损耗'}
        style={{ padding: '0px 20px', marginBottom: 18 }}
        extraContent={
          <div className={styles.titleExtraContent}>
            <div className={styles.rateSettingSty}>
              {/* @ts-ignore */}
              <SettingOutlined color="var(--mx-primary-color)" />
              <span style={{ paddingLeft: 4 }} onClick={() => setLossRateSettingVisible(true)}>
                损耗比例设置
              </span>
            </div>
            <Button type="primary" onClick={() => navigateToEnergyMediumDashboard()}>
              详情数据
            </Button>
          </div>
        }
      ></FormTitle>
      <div style={{ padding: '0px 20px' }}>
        <div className={styles.nameAndTimeSty}>
          <div className={styles.currentSelectedName}>
            <span style={{ color: 'var(--mx-text-secondary-color)' }}>当前选择：</span>
            <span className={styles.nameTextStyle} title={selectedProcessName ?? '--'}>
              {selectedProcessName ?? '--'}
            </span>
          </div>
          <div>
            <span>时间：</span>
            <Space size={8} style={{ marginLeft: 'auto' }}>
              <SwitchDate
                defaultValue={activeKey}
                onChange={(value: TimeActive) => {
                  // 粒度与对应的默认时间范围在同一次交互中更新，避免触发两次查询
                  if (value === TimeActive.CUSTOM) {
                    const currentRange = rangeDate;
                    const days = currentRange[1].diff(currentRange[0], 'day');
                    if (days > 30) {
                      // 超过30天，按结束日期前30日展示
                      const end = currentRange[1];
                      const start = end.clone().subtract(29, 'day').startOf('day');
                      setRangeDate([start, end]);
                    }
                  } else if (value === TimeActive.THIS_WEEK || value === TimeActive.THIS_MONTH) {
                    defaultNodeRangeValue(value, setRangeDate);
                  }
                  setActiveKey(value);
                }}
                options={[
                  { label: '按小时', value: TimeActive.HOUR },
                  { label: '按日', value: TimeActive.CUSTOM },
                  { label: '按周', value: TimeActive.THIS_WEEK },
                  { label: '按月', value: TimeActive.THIS_MONTH },
                ]}
              />
              <RangePicker
                picker={picker}
                value={rangeDate}
                disabledDate={current => current && current > dayjs().startOf('day')}
                onChange={dates => {
                  if (!dates || !dates[0] || !dates[1]) return;
                  onChangePicker(dates as RangeValue, activeKey, setRangeDate);
                }}
                allowClear={false}
              />
            </Space>
          </div>
        </div>
        <div style={{ height: 'calc(80vh - 100px)', overflowY: 'auto' }}>
          {processStatistics && processStatistics.length > 0 ? (
            processStatistics.map((item, index) => (
              <div key={index}>
                <div className={styles.cardContent}>
                  <div className={styles.mainEntryCardContent}>
                    <div>
                      <img
                        src={ImgEnumDisplay[item.energyMediumSceneId as EnergyMediumSceneIds]}
                        className={styles.mainCardImg}
                        alt=""
                      />
                    </div>
                    <div>
                      <span className={styles.mainCardTitle}>
                        {item.energyMediumName ?? '--'}（{unitDisplay(item.energyMediumIndicatorId)}）
                      </span>
                      <span className={styles.mainCardValue}>
                        {!isNil(item.entrySum) ? item.entrySum.toFixed(2) : '--'}
                      </span>
                    </div>
                    <div className={styles.tagSty}>
                      <Tag color="info" border="solid">
                        输入
                      </Tag>
                    </div>
                  </div>
                  <div className={styles.mainExitCardContent}>
                    <span className={styles.mainCardTitle}>
                      {item.energyMediumName ?? '--'}（{unitDisplay(item.energyMediumIndicatorId)}）
                    </span>
                    <span className={styles.mainCardValue}>
                      {!isNil(item.exitSum) ? item.exitSum.toFixed(2) : '--'}
                    </span>
                    <div className={styles.tagSty}>
                      <Tag color="success" border="solid">
                        输出
                      </Tag>
                    </div>
                  </div>
                  <div className={styles.restCardContent}>
                    <div className={styles.restCardLossContent} style={{ borderRight: '1px solid #ffffff60' }}>
                      <div className={styles.restCardContentTitle}>
                        损耗量（{unitDisplay(item.energyMediumIndicatorId)}）
                      </div>
                      <div className={styles.restCardContentValue}>
                        {!isNil(item.lossSum) ? item.lossSum.toFixed(2) : '--'}
                      </div>
                    </div>
                    <div className={styles.restCardLossContent}>
                      <div className={styles.restCardContentTitle}>损耗率</div>
                      <div className={styles.restCardContentValue}>
                        {!isNil(item.lossRate) ? (item.lossRate * 100).toFixed(2) + '%' : '--'}
                      </div>
                    </div>
                  </div>
                </div>
                {item.filterLossDataList && item.filterLossDataList?.length > 0 ? (
                  <div className={styles.lossInfoContent}>
                    <span style={{ color: '#E64242' }}>{dateShow(item.filterLossDataList)}</span>
                    <span>
                      【{selectedProcessName ?? '--'}】内【{item.energyMediumName ?? '--'}】上下游损耗率较高，最高达到
                      {max(item.filterLossDataList?.map(i => Number((i.lossRate && i.lossRate * 100)?.toFixed(2))))}%
                    </span>
                    {!isNil(item.lossAmount) && (
                      <span>
                        ，预计损失<span style={{ color: 'red' }}>{item.lossAmount}</span>元
                      </span>
                    )}

                    <Button
                      type="link"
                      onClick={() =>
                        // onShowLossDetail(item.energyMediumName, item.filterLossDataList, item.energyMediumId)
                        onShowLossDetail(item)
                      }
                    >
                      损耗详情
                      {/* @ts-ignore */}
                      <DoubleRightOutlined />
                    </Button>
                  </div>
                ) : (
                  <div className={styles.nolossInfoContent}>
                    <span>
                      【{selectedProcessName ?? '--'}】内【{item.energyMediumName ?? '--'}
                      】上下游未发现明显损耗和跑冒滴漏现象。
                    </span>
                    <Button
                      type="link"
                      onClick={() =>
                        // onShowLossDetail(item.energyMediumName, item.filterLossDataList, item.energyMediumId)
                        onShowLossDetail(item)
                      }
                    >
                      损耗详情
                      {/* @ts-ignore */}
                      <DoubleRightOutlined />
                    </Button>
                  </div>
                )}
                <LossEcharts
                  activeKey={activeKey}
                  item={item}
                  statisticsDataLoading={statisticsDataLoading}
                  processId={processId}
                  rangeDate={rangeDate}
                  mediumIndicatorDisplayUnitData={mediumIndicatorDisplayUnitData}
                />
              </div>
            ))
          ) : (
            <Spin spinning={statisticsDataLoading}>
              <Empty style={{ marginTop: '20vh' }} />
            </Spin>
          )}
        </div>
      </div>
      <LossRateSettingModal
        lossRateSettingVisible={lossRateSettingVisible}
        onCancel={() => setLossRateSettingVisible(false)}
      />
      <LossDetailModal
        processId={processId}
        lossDetailVisible={lossDetailVisible}
        onCancel={() => setLossDetailVisible(false)}
        selectedProcessName={selectedProcessName}
        currentSelectedLossBasicDetail={currentSelectedLossBasicDetail}
        mediumIndicatorDisplayUnitData={mediumIndicatorDisplayUnitData}
        activeKey={activeKey}
      />
    </>
  );
};

export default LossDuringTransAndDisProcess;
